'use client';

import type { CSSProperties, FC, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { SkinImage } from '../ui/SkinImage';
import { DROP_FLIGHT_MS } from '../caseBattle/showTiming';

interface DropFlightProps {
  hashName: string;
  imageUrl: string | null;
  color: string;
  fromRef: RefObject<HTMLElement | null>;
  toRef: RefObject<HTMLElement | null>;
  containerRef: RefObject<HTMLElement | null>;
  elapsedMs: number;
}

type CssVars = CSSProperties & Record<`--${string}`, string>;

const SIZE = 128;
const SPARKS = [
  [-82, -70], [74, -84], [-108, 6], [110, -12], [-40, -118], [36, 104], [92, 58], [-90, 66], [12, -136], [-16, 122], [128, 26], [-130, -30],
] as const;

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const measure = (from: HTMLElement, to: HTMLElement, container: HTMLElement) => {
  const box = container.getBoundingClientRect();
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const inset = 6;
  return {
    fromX: Math.min(Math.max(a.left + a.width / 2 - box.left - SIZE / 2, inset), box.width - SIZE - inset),
    fromY: a.top + a.height / 2 - box.top - SIZE / 2,
    toX: b.left + b.width / 2 - box.left - SIZE / 2,
    toY: b.top + b.height / 2 - box.top - SIZE / 2,
  };
};

export const DropFlight: FC<DropFlightProps> = ({ hashName, imageUrl, color, fromRef, toRef, containerRef, elapsedMs }) => {
  const [points, setPoints] = useState<ReturnType<typeof measure> | null>(null);

  // WHY: не layout-эффект — оверлей идёт в дереве первым, рефы соседей React вешает позже него
  useEffect(() => {
    const from = fromRef.current;
    const to = toRef.current;
    const container = containerRef.current;
    if (!from || !to || !container || reducedMotion()) return;
    setPoints(measure(from, to, container));
  }, [fromRef, toRef, containerRef]);

  if (!points || elapsedMs >= DROP_FLIGHT_MS) return null;

  const vars: CssVars = {
    '--from-x': `${points.fromX}px`,
    '--from-y': `${points.fromY}px`,
    '--to-x': `${points.toX}px`,
    '--to-y': `${points.toY}px`,
    width: SIZE,
    height: SIZE,
    animationDelay: `${-elapsedMs}ms`,
  };
  const flashStyle: CssVars = { ...vars, background: `radial-gradient(circle, ${color} 0%, ${color}66 45%, transparent 70%)` };
  const waveStyle: CssVars = { ...vars, border: `3px solid ${color}` };
  const sparkStyle = (sx: number, sy: number, i: number): CssVars => ({
    ...vars,
    '--sx': `${sx}px`,
    '--sy': `${sy}px`,
    background: `radial-gradient(circle, #fff 0 2px, ${color} 3px, transparent 6px)`,
    animationDelay: `${-elapsedMs + 60 + (i % 4) * 70}ms`,
  });
  const itemStyle: CssVars = {
    ...vars,
    animation: `shotBattleDropFlight ${DROP_FLIGHT_MS}ms cubic-bezier(0.22, 0.9, 0.3, 1) both`,
    filter: `drop-shadow(0 0 18px ${color}) drop-shadow(0 8px 16px rgba(0,0,0,0.7))`,
  };

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-20">
      <span className="absolute left-0 top-0 rounded-full [animation:shotBattleDropFlash_1100ms_ease-out_both]" style={flashStyle} />
      <span className="absolute left-0 top-0 rounded-full [animation:shotBattleDropWave_900ms_cubic-bezier(0.1,0.8,0.3,1)_both]" style={waveStyle} />
      {SPARKS.map(([sx, sy], i) => (
        <span key={i} className="absolute left-0 top-0 rounded-full [animation:shotBattleSpark_1200ms_ease-out_both]" style={sparkStyle(sx, sy, i)} />
      ))}
      <SkinImage name={hashName} src={imageUrl} alt="" priority className="absolute left-0 top-0" imgClassName="object-contain" style={itemStyle} />
    </div>
  );
};
